import clsx from "clsx";
import { twMerge } from "tailwind-merge";
import { ButtonCross } from "./ButtonCross";
import { ButtonPencil } from "./ButtonPencil";
import { useMemo, useState } from "react";
import { FaSpinner } from "react-icons/fa";
import { ButtonCheck } from "./ButtonCheck";
import { Select } from "../catalyst/select";
import { usePlatformData } from "../hooks/usePlatformData";
import { useCategoryCatalogue } from "../hooks/useCategoryCatalogue";
import {
  livePriorityNames,
  liveSuggestion,
  resolveClassificationRows,
} from "../lib/classificationCategoryResolver";
import type { ProcessingDataClassification } from "@avisoma/shared/dist/ProcessingData";
import { ConfirmDialog } from "./ConfirmDialog";

export function CategoryWithSuggestionApproval({
  category,
  classification,
  onSave,
  color,
  segmentedChildCount,
}: {
  color?: "warning";
  category: string | null | undefined;
  // The page's stored classification ranking. Names inside it are a SNAPSHOT and can be stale
  // (a rename never rewrites them) or dangling (the category was deleted), so this component
  // resolves them against the live catalogue rather than offering them as-is — a save of a
  // name the catalogue no longer holds 400s (DR-3784).
  classification: ProcessingDataClassification | undefined;
  onSave: (category: string | null) => Promise<void>;
  // PRESENCE of this prop marks the page as a segmented PARENT: it keeps no
  // category of its own and a save here is redirected server-side onto every
  // section child. The number is only for wording — 0 is a real state (a
  // fork-crashed or legacy manifest with no `segmentPageIds`), where the
  // backend still resolves the children by `parentPageId`, so the split UI
  // must show without a count rather than fall back to "N/A".
  segmentedChildCount?: number;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const catalogue = useCategoryCatalogue();
  const rows = useMemo(
    () => resolveClassificationRows(classification, catalogue.page),
    [classification, catalogue.page]
  );
  // Both feed ACTIONS, so both stay empty until the catalogue is known. Undefined `suggestion`
  // hides the approve button and falls the label back to its no-suggestion state.
  const suggestion = liveSuggestion(rows, catalogue.page);
  const priorityCategories = livePriorityNames(rows, catalogue.page);

  return (
    <div className="group flex gap-2 items-center">
      {!isEditing && (
        <Label
          category={category}
          suggestion={suggestion}
          color={color}
          segmentedChildCount={segmentedChildCount}
        />
      )}

      {isSaving && (
        <span className="text-xs text-gray-500 dark:text-zinc-400 flex gap-1 items-center">
          <FaSpinner className="animate-spin" /> Saving...
        </span>
      )}

      {isEditing && !isSaving && (
        <CategoryChooser
          category={category}
          onSave={onSave}
          setIsSaving={setIsSaving}
          setIsEditing={setIsEditing}
          priorityCategories={priorityCategories}
          segmentedChildCount={segmentedChildCount}
        />
      )}

      {!isEditing && !isSaving && (
        <div className="md:invisible md:group-hover:visible">
          <ControlButtons
            category={category}
            suggestion={suggestion}
            onSave={onSave}
            setIsEditing={setIsEditing}
            setIsSaving={setIsSaving}
            segmentedChildCount={segmentedChildCount}
          />
        </div>
      )}
    </div>
  );
}

function CategoryChooser({
  category,
  onSave,
  setIsSaving,
  setIsEditing,
  priorityCategories,
  segmentedChildCount,
}: {
  category: string | null | undefined;
  onSave: (category: string | null) => Promise<void>;
  setIsSaving: (isSaving: boolean) => void;
  setIsEditing: (isEditing: boolean) => void;
  /** Already resolved to live names — never a stale or deleted one. */
  priorityCategories: string[];
  segmentedChildCount?: number;
}) {
  const platformData = usePlatformData();
  const [isConfirmingClear, setIsConfirmingClear] = useState(false);

  const saveCategory = async (newCategory: string | null) => {
    try {
      setIsEditing(false);
      setIsSaving(true);
      await onSave(newCategory);
    } catch (err) {
      console.error(err);
      setIsEditing(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-2 items-center">
        <Select
          onChange={(e) => {
            void saveCategory(e.target.value || null);
          }}
          disabled={platformData.categories.isLoading}
          value={category || ""}
        >
          <option value="">(none)</option>

          {priorityCategories.length > 0 && (
            <optgroup label="Maybe this is the right category?">
              {priorityCategories.map((cat) => (
                <option key={cat} value={cat} className="font-bold">
                  {cat}
                </option>
              ))}
            </optgroup>
          )}

          {(platformData.categories.data?.pageCategories ?? []).map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </Select>
        <ButtonCross title="Cancel" onClick={() => setIsEditing(false)} />
      </div>

      {isSegmentedParent(segmentedChildCount) && (
        <div className="flex flex-wrap gap-x-2 items-center text-xs text-gray-500 dark:text-zinc-400">
          <span>{splitHelperText(segmentedChildCount)}</span>
          {/* The Select mirrors the PARENT's category, which is always empty, so
              "(none)" is pre-selected and picking it fires no change event —
              leaving no way to clear the children. This runs the same save with
              null, which the backend propagates as a clear to every section. */}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-gray-700 dark:hover:text-zinc-200"
            onClick={() => setIsConfirmingClear(true)}
          >
            Clear section categories
          </button>
        </div>
      )}

      <ConfirmDialog
        open={isConfirmingClear}
        title="Clear section categories?"
        message={`This clears the category on ${sectionPagesPhrase(
          segmentedChildCount
        )}. This cannot be undone.`}
        confirmLabel="Clear"
        onClose={() => setIsConfirmingClear(false)}
        onConfirm={() => {
          setIsConfirmingClear(false);
          void saveCategory(null);
        }}
      />
    </div>
  );
}

// PRESENCE marks a segmented parent — see the prop comment above. A count of 0
// means "children exist but aren't listed in the manifest", not "no children".
function isSegmentedParent(segmentedChildCount: number | undefined) {
  return segmentedChildCount !== undefined;
}

// A count of 0 is treated the same as "unknown" here (falsy check), matching
// isSegmentedParent's comment: 0 means the manifest doesn't list the children,
// not that there are none — so wording falls back to "all section pages".
function sectionPagesPhrase(segmentedChildCount: number | undefined) {
  return !segmentedChildCount
    ? "all section pages"
    : `all ${segmentedChildCount} section ${
        segmentedChildCount === 1 ? "page" : "pages"
      }`;
}

function splitLabelText(segmentedChildCount: number | undefined) {
  if (!segmentedChildCount) {
    return "Split into sections";
  }

  return `Split into ${segmentedChildCount} section${
    segmentedChildCount === 1 ? "" : "s"
  }`;
}

function splitHelperText(segmentedChildCount: number | undefined) {
  return `Applies to ${sectionPagesPhrase(
    segmentedChildCount
  )} — this parent page keeps no category of its own.`;
}

function ControlButtons({
  category,
  suggestion,
  onSave,
  setIsEditing,
  setIsSaving,
  segmentedChildCount,
}: {
  category: string | null | undefined;
  suggestion: string | undefined;
  onSave: (category: string) => Promise<void>;
  setIsEditing: (isEditing: boolean) => void;
  setIsSaving: (isSaving: boolean) => void;
  segmentedChildCount?: number;
}) {
  if (category) {
    return <ButtonPencil onClick={() => setIsEditing(true)} />;
  }

  if (suggestion) {
    return (
      <div className="flex gap-1 items-center">
        <ButtonCheck
          onClick={() => {
            setIsSaving(true);
            onSave(suggestion).finally(() => {
              setIsSaving(false);
            });
          }}
        />
        <ButtonCross
          title="Reject"
          onClick={() => {
            setIsEditing(true);
          }}
        />
      </div>
    );
  }

  // A segmented parent has no category of its own to pencil-edit, but the
  // chooser is still how a curator bulk-sets every section child from here —
  // so give it an edit affordance with the bulk-action title.
  if (isSegmentedParent(segmentedChildCount)) {
    return (
      <ButtonPencil
        title="Set category for all section pages"
        onClick={() => setIsEditing(true)}
      />
    );
  }

  // Uncategorized ordinary pages (including dead rank-1 winners) still need
  // a way into the chooser — liveSuggestion() correctly offers nothing to
  // approve, but that must not hide the edit path (DR-4068).
  return <ButtonPencil onClick={() => setIsEditing(true)} />;
}

function Label({
  category,
  suggestion,
  color,
  segmentedChildCount,
}: {
  category: string | null | undefined;
  suggestion: string | undefined;
  color?: "warning";
  segmentedChildCount?: number;
}) {
  // A segmented parent legitimately has no category of its own — say so instead
  // of falling through to the "N/A" missing-value state.
  const isSplitPlaceholder =
    !category && !suggestion && isSegmentedParent(segmentedChildCount);

  const getText = () => {
    if (category) {
      return category;
    }

    if (suggestion) {
      return `${suggestion} ?`;
    }

    if (isSplitPlaceholder) {
      return splitLabelText(segmentedChildCount);
    }

    return "N/A";
  };

  return (
    <span
      className={twMerge(
        clsx(
          "text-sm bg-blue-400 dark:bg-blue-400 text-white px-2 py-0.5 rounded-xl whitespace-nowrap",
          {
            "bg-green-400 dark:bg-green-400": !!category,
            "bg-yellow-400 dark:bg-yellow-400": color === "warning",
            "bg-gray-100 dark:bg-zinc-700 text-gray-800 dark:text-zinc-100":
              isSplitPlaceholder,
          }
        )
      )}
    >
      {getText()}
    </span>
  );
}
